/**
 * Pre-Journey Plan: a monthly visit plan for the people of the roles a project ticks (Assistant
 * Managers to start). The shapes mirror what /api/pre-journey-plan answers; the server builds the
 * plan and every figure in the report, the web pages only draw them.
 */

/** How often a rule's visits are due: every month of the quarter, in its first two months, or once a quarter. */
export type PlanCadence = 'per_month' | 'months_1_2' | 'per_quarter';
/** A store's one priority, first match wins in this order. */
export type PlanPriority = 'new_ipro' | 'pip' | 'red_flags' | 'score_declining';
/** Inside = the store is in the person's base city; upcountry = anywhere else. */
export type PlanWhere = 'inside' | 'upcountry';

export const PLAN_PRIORITIES: PlanPriority[] = ['new_ipro', 'pip', 'red_flags', 'score_declining'];
export const PLAN_WHERES: PlanWhere[] = ['upcountry', 'inside'];
export const PLAN_CADENCES: PlanCadence[] = ['per_month', 'months_1_2', 'per_quarter'];

export interface PlanRule {
  enabled: boolean;
  /** Whole number, 1 to 10. */
  visits: number;
  cadence: PlanCadence;
}

export type PlanRules = Record<PlanWhere, Record<PlanPriority, PlanRule>>;

export interface PlanSettings {
  /** The roles that get a plan; empty = the plan is off. */
  roleIds: string[];
  rules: PlanRules;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface PlanOverCapacity {
  employeeCode: string;
  name: string;
  /** This month's visits due. */
  due: number;
  /** What the role's current visit target allows this month. */
  capacity: number;
}

/** GET (and PUT) /pre-journey-plan/:projectId/settings. */
export interface PlanSettingsResponse {
  settings: PlanSettings;
  roles: { id: string; name: string }[];
  /** YYYY-MM, the current month. */
  planMonth: string;
  /** When the current month's plan was built; null while it has not been. */
  builtAt: string | null;
  /** Planned people with no base city. */
  missingBaseCity: number;
  overCapacity: PlanOverCapacity[];
  /** Set when this month is not built because building it failed just now. */
  buildError?: string | null;
}

export interface PlanBaseCityPerson {
  employeeCode: string;
  name: string;
  roleName: string;
  /** The city of the person's 1Office position; null when it has none. */
  positionCity: string | null;
  /** The city an admin set here; null when none was set. */
  overrideCity: string | null;
}

/** GET (and PUT) /pre-journey-plan/:projectId/base-cities. */
export interface PlanBaseCitiesResponse {
  people: PlanBaseCityPerson[];
  /** The project's store cities, sorted, offered while typing. */
  cityOptions: string[];
  /** After a save: set when the cities were saved but this month could not be rebuilt. */
  buildError?: string | null;
}

export interface PlanBaseCityChange {
  employeeCode: string;
  /** null removes the override. */
  city: string | null;
}

export type PlanPersonStatus = 'met' | 'on_track' | 'behind' | 'no_base_city' | 'nothing_due';

export interface PlanStoreRow {
  /** The store's id (a UUID). */
  storeId: string;
  storeName: string;
  storeCode: string | null;
  city: string | null;
  location: PlanWhere;
  priority: PlanPriority;
  redFlagCount: number | null;
  cadence: PlanCadence;
  due: number;
  done: number;
  /** YYYY-MM-DD. */
  lastVisit: string | null;
}

export interface PlanPerson {
  employeeCode: string;
  name: string;
  roleName: string;
  baseCity: string | null;
  baseCitySource: 'override' | '1office' | null;
  status: PlanPersonStatus;
  storesInside: number;
  storesUpcountry: number;
  due: number;
  done: number;
  achievedPct: number | null;
  behindBy: number;
  stores: PlanStoreRow[];
}

/** GET /pre-journey-plan/:projectId/report?month=YYYY-MM | ?quarter=YYYY-Qn. */
export interface PlanReport {
  period: { kind: 'month' | 'quarter'; label: string; from: string; to: string; elapsedShare: number; ended: boolean };
  planOn: boolean;
  /** YYYY-MM months of the period that have a plan. */
  builtMonths: string[];
  totals: { people: number; due: number; done: number; achievedPct: number | null };
  people: PlanPerson[];
  /** Set when the period's current month is not built because building it failed just now. */
  buildError?: string | null;
}

/** The period a report is asked for; left out, the server answers with the current month. */
export type PlanReportPeriod = { month: string } | { quarter: string };
